import { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box, Button, Card, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Avatar,
  IconButton, Tooltip, Skeleton, Stack, InputAdornment,
} from '@mui/material';
import { Refresh as RefreshIcon, Edit as EditIcon, Search as SearchIcon, PersonOutline as PersonIcon } from '@mui/icons-material';

// Components
import { PageHeader } from '@/common-components/layout';

// Hooks
import { useUsers, useRoles, useUrlPagination, useUrlState } from '@/common-hooks';
import { useAuthentication } from '@/common-hooks/use-authentication';

// Types
import type { User } from '@/common-types';

// Constants
import { PERMISSIONS } from '@/common-constants/permissions';

export default function Users() {
  const { hasPermission } = useAuthentication();
  const canManageUsers = hasPermission(PERMISSIONS.USER_MANAGEMENT);
  const { users, loading, error, refresh, updateUser } = useUsers();
  const { roles, loading: rolesLoading, refresh: refreshRoles } = useRoles();
  
  const { page, rowsPerPage, setPage, setRowsPerPage } = useUrlPagination('users');
  const { value: searchQuery, setValue: setSearchQuery } = useUrlState('q', '');
  
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [updating, setUpdating] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const fetchingRef = useRef(false);
  useEffect(() => {
    if (canManageUsers && !fetchingRef.current) {
      fetchingRef.current = true;
      refresh().finally(() => { fetchingRef.current = false; });
    }
  }, [canManageUsers, refresh]);

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      // Users with lastActivityAt come first, sorted desc; users without go to end
      if (a.lastActivityAt && b.lastActivityAt) {
        return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
      }
      if (a.lastActivityAt) return -1;
      if (b.lastActivityAt) return 1;
      return 0;
    });
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return sortedUsers;
    const q = searchQuery.toLowerCase();
    return sortedUsers.filter((u) => 
      u.email.toLowerCase().includes(q) || 
      u.name?.toLowerCase().includes(q) || 
      u.role.toLowerCase().includes(q)
    );
  }, [sortedUsers, searchQuery]);

  const paginatedUsers = useMemo(() => 
    filteredUsers.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredUsers, page, rowsPerPage]
  );

  const handleRefresh = () => {
    refresh();
    refreshRoles();
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowEditModal(true);
    // Fetch roles only if not already loaded
    if (roles.length === 0) {
      refreshRoles();
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;
    try {
      setUpdating(true);
      await updateUser(selectedUser.userId, { role: newRole });
      setShowEditModal(false);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  if (!canManageUsers) {
    return <Alert severity="warning">You need user management permission to access this page.</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle={`${filteredUsers.length} users`}
        actions={
          <>
            <TextField
              size="small"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 200 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
          </>
        }
      />

      {(error || localError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLocalError(null)}>
          {error || localError}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table 
            size="small"
            sx={{ 
              tableLayout: 'auto', // Auto-size columns based on content
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>User</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Role</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Last Logged In</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Created</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Stack direction="row" gap={1.5} alignItems="center"><Skeleton variant="circular" width={32} height={32} /><Box><Skeleton width={120} /><Skeleton width={160} height={12} /></Box></Stack></TableCell>
                    <TableCell><Skeleton width={60} /></TableCell>
                    <TableCell><Skeleton width={100} /></TableCell>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell align="right"><Skeleton width={30} /></TableCell>
                  </TableRow>
                ))
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box sx={{ py: 6, textAlign: 'center' }}>
                      <PersonIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                      <Box color="text.secondary">{searchQuery ? 'No matches' : 'No users'}</Box>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Stack direction="row" gap={1.5} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.8rem' }}>
                          {(user.name || user.email).charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Box sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{user.name || 'No name'}</Box>
                          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{user.email}</Box>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip label={user.role} size="small" color="primary" /></TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                      {user.lastActivityAt
                        ? new Date(user.lastActivityAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                        : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>{new Date(user.createdAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEditClick(user)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredUsers.length > 10 && (
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Card>

      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Role</DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            <TextField label="Email" value={selectedUser?.email || ''} disabled size="small" fullWidth />
            <TextField 
              select 
              label="Role" 
              value={newRole} 
              onChange={(e) => setNewRole(e.target.value)} 
              size="small" 
              fullWidth
              disabled={rolesLoading}
              helperText={rolesLoading ? 'Loading roles...' : ''}
            >
              {roles.map((r) => <MenuItem key={r.roleId} value={r.roleId}>{r.name}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditModal(false)} disabled={updating}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateRole} disabled={updating || rolesLoading || !newRole}>
            {updating ? <Skeleton variant="text" width={50} height={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
